/**
 * useTeamLiveRevision — turns Firestore change signals into a revision number
 * the Team & Roles surfaces re-fetch on.
 *
 * The React half of the teamLive trio: policy leaf (rules, constants) →
 * repository (the only onSnapshot) → this hook. Consumers do:
 *
 *   const rev = useTeamLiveRevision({ accountId, kinds, enabled });
 *   useEffect(() => { if (rev > 0) load({ background: true }); }, [rev]);
 *
 * `rev` starts at 0 and never bumps for the initial snapshot, so a mount costs
 * exactly one load — the one the component already does. It RESETS to 0 when
 * the account changes, which is the bug VmLifecycleBanner documents from
 * resolving an account id once and leaving a listener pointed at the previous
 * workspace.
 *
 * Listener idiom follows useTrialStatus: reset before the guard, try/catch
 * around setup, cleanup returns the unsubscribe, errors never throw.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TEAM_LIVE_DEBOUNCE_MS,
  TEAM_LIVE_DISABLE_KEY,
  currentUsageMonthKey,
  isTeamLiveEnabled,
  type TeamLiveKind,
} from '../services/teamLive/teamLivePolicy';
import { subscribeTeamChanges } from '../repositories/teamLiveRepository';

/** How often we re-check whether the UTC month (the usage doc id) has rolled. */
const MONTH_ROLLOVER_TICK_MS = 60_000;

function readDisableFlag(): string | null {
  try {
    return window.localStorage.getItem(TEAM_LIVE_DISABLE_KEY);
  } catch {
    return null;
  }
}

export interface UseTeamLiveRevisionOptions {
  /** The organization to watch. Null while it resolves — nothing subscribes. */
  accountId: string | null;
  /** Already narrowed to what this viewer may read (see subscribableKinds). */
  kinds: readonly TeamLiveKind[];
  /** Caller-side gate, e.g. "only while the usage view is open". */
  enabled?: boolean;
  debounceMs?: number;
}

export function useTeamLiveRevision({
  accountId,
  kinds,
  enabled = true,
  debounceMs = TEAM_LIVE_DEBOUNCE_MS,
}: UseTeamLiveRevisionOptions): number {
  const [revision, setRevision] = useState(0);

  // A caller passing a fresh array literal every render must not resubscribe
  // every render. Sort+join to a stable key, and rebuild the list from it.
  const kindsKey = useMemo(() => Array.from(new Set(kinds)).sort().join(','), [kinds]);
  const needsUsage = kindsKey.split(',').includes('accountUsage');

  const [usageMonthKey, setUsageMonthKey] = useState(() => currentUsageMonthKey(Date.now()));
  useEffect(() => {
    if (!needsUsage || !enabled) return;
    const id = setInterval(() => {
      const next = currentUsageMonthKey(Date.now());
      setUsageMonthKey((prev) => (prev === next ? prev : next));
    }, MONTH_ROLLOVER_TICK_MS);
    return () => clearInterval(id);
  }, [needsUsage, enabled]);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Reset first: a different organization (or a disabled feature) must never
    // hand the consumer a revision earned by the previous one.
    setRevision(0);

    const list = kindsKey ? (kindsKey.split(',') as TeamLiveKind[]) : [];
    if (!accountId || !enabled || list.length === 0) return;
    if (!isTeamLiveEnabled(readDisableFlag())) return;

    let unsub: (() => void) | null = null;
    try {
      unsub = subscribeTeamChanges(
        accountId,
        list,
        () => {
          // Coalesce a burst (one batch writes a member doc, its role bindings
          // and the invitation doc) into a single refetch.
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setRevision((r) => r + 1);
          }, debounceMs);
        },
        needsUsage ? { usageMonthKey } : {},
      );
    } catch {
      unsub = null;
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (unsub) unsub();
    };
  }, [accountId, kindsKey, enabled, debounceMs, needsUsage, usageMonthKey]);

  return revision;
}

export default useTeamLiveRevision;
